import React, { useEffect, useState } from 'react';
import { Badge, Button, Input, Modal, Space, Table, Typography, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { SearchOutlined } from '@ant-design/icons';
import {
  ClienteCoincidente,
  CoincidentesResultado,
  buscarCoincidentes,
  interpretarError,
} from '../../../api/altasCasosApi';
import { fctColors } from '../../../estilo-corporativo-fct';

interface Props {
  open: boolean;
  /** Prefill con los datos de la solicitud (gesto habitual: buscar por lo que llegó). */
  emailInicial?: string | null;
  movilInicial?: string | null;
  nombreInicial?: string | null;
  /** Cliente provisional del propio candidato: se excluye del resultado (se busca un existente). */
  idClienteExcluir?: number;
  onSeleccionar: (candidato: ClienteCoincidente) => void;
  onCerrar: () => void;
}

/**
 * Popup de búsqueda de coincidentes (spec §6.3, ajuste 7 firme): filtros por email, teléfono,
 * móvil y nombre completo, nº de coincidencias POR PARÁMETRO y resultado en tabla
 * (nombre/apellidos/provincia/teléfono/email) con selección por clic en fila.
 */
export const BusquedaCoincidentesModal: React.FC<Props> = ({
  open,
  emailInicial,
  movilInicial,
  nombreInicial,
  idClienteExcluir,
  onSeleccionar,
  onCerrar,
}) => {
  const [codigo, setCodigo] = useState('');
  const [email, setEmail] = useState(emailInicial ?? '');
  const [telefono, setTelefono] = useState('');
  const [movil, setMovil] = useState(movilInicial ?? '');
  const [nombre, setNombre] = useState(nombreInicial ?? '');
  const [buscando, setBuscando] = useState(false);
  const [resultado, setResultado] = useState<CoincidentesResultado | null>(null);

  /**
   * El modal NUNCA se desmonta (AltasCasoPage lo mantiene siempre montado; `destroyOnClose` solo
   * destruye el DOM interno de AntD, no este componente), así que sin este efecto el estado
   * (criterios + resultado de la búsqueda anterior) sobrevivía entre aperturas y el prefill de
   * email/móvil/nombre solo se leía una vez al primer montaje — quedando desactualizado si el
   * provisional se editaba después. Al abrir: resincroniza el prefill y limpia lo demás.
   */
  useEffect(() => {
    if (!open) return;
    setCodigo('');
    setEmail(emailInicial ?? '');
    setTelefono('');
    setMovil(movilInicial ?? '');
    setNombre(nombreInicial ?? '');
    setResultado(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const buscar = async () => {
    if (![codigo, email, telefono, movil, nombre].some((v) => v.trim())) {
      message.warning('Indique al menos un criterio de búsqueda.');
      return;
    }
    setBuscando(true);
    try {
      setResultado(
        await buscarCoincidentes({
          codigo: codigo.trim() || undefined,
          email: email.trim() || undefined,
          telefono: telefono.trim() || undefined,
          movil: movil.trim() || undefined,
          nombre: nombre.trim() || undefined,
          idClienteExcluir,
        }),
      );
    } catch (error) {
      message.error(interpretarError(error).mensaje);
    } finally {
      setBuscando(false);
    }
  };

  const criterio = (
    label: string,
    valor: string,
    setValor: (v: string) => void,
    conteo: number | undefined,
  ) => (
    <Space direction="vertical" size={2} style={{ width: '100%' }}>
      <Typography.Text type="secondary" style={{ fontSize: 12 }}>
        {label}{' '}
        {resultado && (
          // Verde solo si hay coincidencias; gris neutro cuando es 0 (el rojo por defecto de AntD
          // daría sensación de error).
          <Badge
            count={conteo ?? 0}
            showZero
            overflowCount={999}
            style={{ backgroundColor: (conteo ?? 0) > 0 ? fctColors.success : fctColors.estadoDescartado }}
          />
        )}
      </Typography.Text>
      <Input value={valor} onChange={(e) => setValor(e.target.value)} onPressEnter={buscar} allowClear />
    </Space>
  );

  const columns: ColumnsType<ClienteCoincidente> = [
    { title: 'Código', dataIndex: 'idCodigoGf', key: 'codigo', width: 120 },
    {
      // Columna ancha (sin width fijo, se lleva el espacio sobrante): nombre + apellidos.
      title: 'Cliente',
      key: 'cliente',
      render: (_, c) => `${c.nombre ?? ''} ${c.apellido1 ?? ''} ${c.apellido2 ?? ''}`.trim(),
    },
    { title: 'Provincia', dataIndex: 'provincia', key: 'provincia', width: 130 },
    { title: 'Teléfono', dataIndex: 'telefono', key: 'telefono', width: 130 },
    { title: 'Email', dataIndex: 'email', key: 'email', width: 220, ellipsis: true },
  ];

  return (
    <Modal
      title="Buscar cliente existente"
      open={open}
      onCancel={onCerrar}
      footer={null}
      width={1120}
      destroyOnClose
    >
      <Space direction="vertical" size={16} style={{ width: '100%' }}>
        {/* Criterios en orden Código, Nombre-Apellidos, Email, Teléfono, Móvil; Nombre y Email más anchos. */}
        <div style={{ display: 'grid', gridTemplateColumns: '1.1fr 2fr 2fr 1fr 1fr auto', gap: 12, alignItems: 'end' }}>
          {criterio('Código', codigo, setCodigo, resultado?.porCodigo)}
          {criterio('Nombre-Apellidos', nombre, setNombre, resultado?.porNombre)}
          {criterio('Email', email, setEmail, resultado?.porEmail)}
          {criterio('Teléfono', telefono, setTelefono, resultado?.porTelefono)}
          {criterio('Móvil', movil, setMovil, resultado?.porMovil)}
          <Button type="primary" icon={<SearchOutlined />} loading={buscando} onClick={buscar}>
            Buscar
          </Button>
        </div>

        <Table<ClienteCoincidente>
          rowKey="idCliente"
          size="small"
          columns={columns}
          dataSource={resultado?.candidatos ?? []}
          loading={buscando}
          pagination={false}
          locale={{ emptyText: resultado ? 'Sin coincidencias' : 'Introduzca criterios y pulse Buscar' }}
          onRow={(c) => ({
            style: { cursor: 'pointer' },
            onClick: () => onSeleccionar(c),
          })}
        />
      </Space>
    </Modal>
  );
};
